// The PF (EPFO ECR) return cycle for one wage month, as five numbered steps.
//
// Reads the same visual language as the salary-TDS steppers in TdsTab.tsx
// (numbered markers, a connector between them, an underlined action under
// each). Kept as its own component rather than shared with that file because
// the states differ: TDS has done/active/todo, PF also has "blocked", which
// is step 1 saying the data is not ready — a different thing from a step
// merely being out of reach.
//
// This component decides NOTHING about progress. Every state comes from
// GET /pf-return, which derives it from facts read off the portal. If it
// looks wrong here, the fact behind it is what to go and look at.
import React from "react";

import type { PfBlocker, PfReturnState, PfStepKey } from "../../api/pfReturn";
import StatutorySteps, { StepNote, STEP_CSS } from "./StatutorySteps";

const BLOCKER_TEXT: Record<PfBlocker, string> = {
  salary_not_approved: "approve the month's salaries first",
  uan_missing: "some employees have no UAN",
  no_members: "nobody to file for this month",
};

const ACTION_CLASS = "pf-step-btn";

export interface PfStepActions {
  onUpload: () => void;
  /** Open the portal's returns table and stop — a look, nothing submitted. */
  onApprove: () => void;
  /** Walk to the same table and click Approve on the portal. */
  onApproveSubmit: () => void;
  onChallan: () => void;
  /** Sign in and stop — no filing, no walking the portal. */
  onLogin: () => void;
  loggingIn: boolean;
  busy: PfStepKey | null;
  /** The extension's last word, rendered by the page that owns the run. */
  portalSays?: React.ReactNode;
}

/** The short line under each step's name: what it is waiting on, or what the
 *  portal already told us. */
function noteFor(key: PfStepKey, data: PfReturnState): React.ReactNode {
  const { readiness, portal } = data;

  if (key === "prepare") {
    if (readiness.blockers.length) {
      const why = readiness.blockers.map((b) => BLOCKER_TEXT[b]).join("; ");
      // Naming the people is the point — "some employees" is not actionable.
      const who = readiness.missing_uan.length
        ? ` (${readiness.missing_uan.slice(0, 3).join(", ")}${readiness.missing_uan.length > 3 ? `, +${readiness.missing_uan.length - 3} more` : ""})`
        : "";
      return <StepNote tone="warn">{why}{who}</StepNote>;
    }
    return <StepNote>{readiness.members} member{readiness.members === 1 ? "" : "s"} · ₹{readiness.epf_contribution.toLocaleString("en-IN")} EPF</StepNote>;
  }

  if (key === "upload") {
    if (portal?.return_file_id) {
      return <StepNote>#{portal.return_file_id} · {portal.return_type || "?"} · {portal.contribution_rate || "?"}%</StepNote>;
    }
    return null;
  }

  if (key === "approve") {
    if (!portal?.portal_status) return null;
    if (!portal.portal_status_recognised) {
      // Deliberately quotes the portal rather than interpreting it: an
      // unseen status is something to show, not something to assume about.
      return <StepNote tone="warn">portal says “{portal.portal_status}” — not recognised</StepNote>;
    }
    return <StepNote>{portal.portal_status}</StepNote>;
  }

  if (key === "challan") {
    if (portal?.challan_paid_at) return <StepNote>paid</StepNote>;
    // A TRRN with no CRN beside it means the challan exists but the money has
    // not moved — worth saying, because the portal looks "done" at that point.
    if (portal?.trrn) return <StepNote tone="warn">prepared, not paid yet</StepNote>;
    return null;
  }

  if (key === "file") {
    // Paying the challan is what files the return, so this step has no action
    // of its own — it carries the two numbers that prove it happened.
    if (!portal?.challan_paid_at) return <StepNote>once the challan is paid</StepNote>;
    return (
      <>
        {portal.trrn && <StepNote>TRRN {portal.trrn}</StepNote>}
        {portal.challan_reference && <StepNote>CRN {portal.challan_reference}</StepNote>}
      </>
    );
  }

  return null;
}

export default function PfReturnSteps({
  data,
  actions,
}: {
  data: PfReturnState;
  actions: PfStepActions;
}) {
  const action = (key: PfStepKey): React.ReactNode => {
    const step = data.steps.find((s) => s.key === key);
    if (!step) return null;
    const running = actions.busy === key;
    // Only a run already in flight disables anything: the extension drives one
    // portal window, so a second run would fight the first for it.
    const disabled = actions.busy !== null;

    // Greyed when this step's turn has not come. Everything stays a plain
    // underlined link — a filled box here fought with the numbered markers
    // and read as a control that had somehow broken.
    const waiting = step.state === "locked" || step.state === "blocked";
    const button = (label: string, onClick: () => void) => (
      <button
        type="button"
        className={`${ACTION_CLASS}${waiting ? " is-waiting" : ""}`}
        onClick={onClick}
        disabled={disabled}
        title={waiting ? "Earlier steps are not done yet — you can still run this." : undefined}
      >
        {running ? "Working…" : label}
      </button>
    );

    if (key === "upload") {
      return button(step.state === "done" ? "Upload again" : "Upload data", actions.onUpload);
    }
    if (key === "approve") {
      // Side by side, in the order they are meant to be used: look first,
      // approve second.
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
          {button(step.state === "done" ? "Re-check" : "Open on portal", actions.onApprove)}
          {step.state !== "done" && button("Approve", actions.onApproveSubmit)}
        </div>
      );
    }
    if (key === "challan") return button("Generate challan", actions.onChallan);
    return null;
  };

  return (
    <div className="gt-card p-5">
      <style>{STEP_CSS}</style>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <span className="gt-eyebrow">EPFO return</span>
        {/* The sign-in lives with the filing it serves, not in the page
            header. It is not a step of the cycle -- it is what you reach
            for when the cycle cannot get past the portal -- but it belongs
            to EPFO, and a page-wide button said nothing about which portal
            it meant once ESI arrived beside it. */}
        <button type="button" className="gt-portal-btn"
          disabled={actions.busy !== null || actions.loggingIn}
          title="Open the EPFO portal and sign in with the saved credentials. You solve the CAPTCHA; the extension clears the popups and stops there."
          onClick={actions.onLogin}>
          {actions.loggingIn ? "Opening portal\u2026" : "Login to portal"}
        </button>
      </div>
      <p className="gt-page-sub" style={{ fontSize: 12, margin: "4px 0 14px" }}>
        One wage month, start to finish. Each step unlocks when the one before it
        is done on the portal — Teamlio reads that from EPFO, it is never ticked here.
      </p>

      <StatutorySteps
        steps={data.steps.map((step) => ({
          key: step.key,
          // "File return" reads as something still to do; once the challan
          // is paid it has already happened, so the step says so.
          title: step.key === "file" && step.state === "done" ? "Filed" : step.title,
          state: step.state,
          note: noteFor(step.key, data),
          action: action(step.key),
        }))}
      />

      {actions.portalSays}
    </div>
  );
}
